import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageLoader, InlineSpinner } from "@/components/common/Loading";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { onAuthed } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token"); return; }
    api.get(`/auth/invitation/${token}`).then((r) => setInvite(r.data)).catch((e) => setError(apiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invitation", { token, password });
      setToken(data.access_token);
      await onAuthed(data);
      navigate(data.mfa_setup_required ? "/security?setup=1" : "/");
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  if (error && !invite) return <AuthLayout title="Invitation error"><p className="text-sm text-rose-600" data-testid="auth-error">{error}</p>
    <p className="text-sm text-slate-600 mt-4">Use <b>Forgot password</b> with the email address that received your invitation to set your password, or ask your recruiter to resend the invitation.</p>
    <div className="flex gap-4 mt-4 text-sm font-medium"><Link to="/forgot-password" className="text-[#0093D6] hover:underline" data-testid="invite-error-forgot">Forgot password</Link><Link to="/login" className="text-[#0093D6] hover:underline" data-testid="invite-error-login">Back to sign in</Link></div></AuthLayout>;
  if (!invite) return <AuthLayout title="Accepting invitation"><PageLoader label="Loading invitation" /></AuthLayout>;

  return (
    <AuthLayout title={`Join ${invite.account_name}`} subtitle="Verify your email and set a password to activate your account."
      tagline={`Welcome to ${invite.account_name} on Top-Recruit.`}>
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-rose-600" data-testid="auth-error">{error}</p>}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Name</Label>
          <Input value={invite.name} disabled className="mt-1.5 bg-slate-50" data-testid="invite-name" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Email</Label>
          <Input value={invite.email} disabled className="mt-1.5 bg-slate-50" data-testid="invite-email" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Create password</Label>
          <Input type="password" value={password} required data-testid="invite-password"
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="At least 8 characters" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Confirm password</Label>
          <Input type="password" value={confirm} required data-testid="invite-confirm"
            onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
        </div>
        <Button type="submit" disabled={loading} data-testid="invite-submit" className="w-full bg-[#006090] hover:bg-[#004E75]">
          {loading ? <InlineSpinner /> : "Activate account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
